import { reflect } from '@effector/reflect';
import { NumberFormatter } from '@mantine/core';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $t, translated } from 'shared/lib';
import { createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import { PROMOCODE_ITEM_STATUSES } from '../../config';
import { PromocodeRegistryModel } from '../../model';
import cls from './promocode-registry.module.scss';

export type PromocodeRegistryProps = RegistryRootProps & {
	topBarActionSlot?: ReactNode;
	emptyActionSlot?: ReactNode;
};

export const createPromocodeRegistry = (model: PromocodeRegistryModel) => {
	const Registry = createRegistry(model);

	const StatusesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'promocode-registry-statuses-filter',
			className: cls.promocodeRegistryStatusesFilter,
			data: $t.map((t) =>
				Object.values(PROMOCODE_ITEM_STATUSES).map((status) => ({
					value: status,
					label: t(`promocode-item-statuses.${status}`),
				})),
			),
			value: model.filters.statuses.$value,
			onChange: (value) => model.filters.statuses.setValue(value),
			label: translated('promocode-registry.filters.statuses.label'),
			placeholder: translated('promocode-registry.filters.statuses.placeholder'),
			nothingFoundMessage: translated('promocode-registry.filters.statuses.empty'),
			searchable: true,
			clearable: true,
		},
	});

	const PromocodeRegistry = forwardRef<HTMLDivElement, PromocodeRegistryProps>(
		({ emptyActionSlot, topBarActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.TopBar className={cls.promocodeRegistryTopBar}>
						<Registry.Filters className={cls.promocodeRegistryFilters}>
							<StatusesFilter />
						</Registry.Filters>
						{topBarActionSlot}
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='code'>
									{t('promocode-registry.table-columns.code')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('promocode-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='deposit'>
									{t('promocode-registry.table-columns.deposit')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='wagering'>
									{t('promocode-registry.table-columns.wagering')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='status'>
									{t('promocode-registry.table-columns.status')}
								</Registry.Table.ThSorter>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>{row.code}</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.currentDeposit} /> /{' '}
											<NumberFormatter value={row.totalDeposit} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.currentWagering} /> /{' '}
											<NumberFormatter value={row.totalWagering} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											{t(`promocode-item-statuses.${row.status}`, {
												defaultValue: row.status,
											})}
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('promocode-registry.empty.title')}
						message={t('promocode-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('promocode-registry.table-empty.title')}
						message={t('promocode-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return PromocodeRegistry;
};
